import { Card } from '@design/components/card/Card';
import { Icon } from '@design/components/icon/Icon';
import { Formats, formatDateToLocale } from '@helpers/utils/dates/dates';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import clsx from 'clsx';
import { intlFormat } from 'date-fns';
import { useTranslation } from 'react-i18next';

import styles from './EventDate.module.scss';

export interface EventDateProps {
  dateString: string;
  reported?: boolean;
  companyEvent?: boolean;
}

export const EventDate = ({
  dateString,
  reported,
  companyEvent,
}: EventDateProps) => {
  const reportingDate = new Date(dateString);
  const languageSetting = useLanguageStore(state => state.languageSetting);

  const hasTime = dateString.includes('T');
  const { t } = useTranslation('calendar');

  const hourFormatted = intlFormat(reportingDate, {
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
    hour12: false,
  });

  const dateFormatted = formatDateToLocale(
    languageSetting,
    reportingDate,
    Formats['EEEE, MMMM d']
  );

  const getLabel = () => {
    if (companyEvent) {
      return;
    }

    if (reported) {
      return <span data-testid="reported">{t('calendar:reported')}</span>;
    }

    if (hasTime) {
      return (
        <span data-testid="scheduled-at" suppressHydrationWarning>
          {t('calendar:scheduledAt', {
            hourFormatted,
          })}
        </span>
      );
    }

    return (
      <span data-testid="scheduled-for" suppressHydrationWarning>
        {t('calendar:scheduledFor')}
      </span>
    );
  };

  return (
    <div className={styles.eventDateRoot} data-testid="earnings-date">
      <Card size="small">
        <div className={clsx(styles.title, { [styles.reported]: reported })}>
          {!companyEvent && (
            <Icon
              iconName={reported ? 'CheckmarkLightCircle' : 'Calendar'}
              size={16}
            />
          )}
          {getLabel()}
        </div>
        <div className={styles.description} suppressHydrationWarning>
          {dateFormatted}
        </div>
      </Card>
    </div>
  );
};
